import { manifestApi } from "#/api/manifestApi";
import SelectFile from "#/components/inputs/SelectFile";
import { showSuccessNotification } from "#/components/notifications";
import type { ImportCommitResult, ImportPreview, ImportPreviewRow, ImportRowState } from "#/types/manifest";
import { formatHawbLabel } from "#/utils/manifestUtils";
import { logger } from "#/utils/logger";
import { Badge, Button, Checkbox, Modal, Progress, ScrollArea } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import HawbChangeSummary from "./HawbChangeSummary";

interface ImportManifestModalProps {
  opened: boolean;
  onClose: () => void;
  onImported: () => void;
}

const STATE_ORDER: ImportRowState[] = ["error", "changed", "new", "unchanged"];

const STATE_COLORS: Record<ImportRowState, string> = {
  new: "green",
  changed: "orange",
  unchanged: "gray",
  error: "red",
};

/**
 * Manifest import: pick an .xlsx / .csv, review what each row would do, tick
 * which existing consignments to override, then confirm. Nothing is written
 * before Confirm; the server re-reads the file on commit.
 */
const ImportManifestModal = ({ opened, onClose, onImported }: ImportManifestModalProps) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [overrideIds, setOverrideIds] = useState<number[]>([]);
  const [result, setResult] = useState<ImportCommitResult | null>(null);

  const reset = () => {
    setFile(null);
    setProgress(0);
    setPreview(null);
    setOverrideIds([]);
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const previewMutation = useMutation({
    mutationFn: (selected: File) => manifestApi.importPreview(selected, setProgress),
    onSuccess: (response) => setPreview(response.data),
    onError: (error) => logger.error("manifest import preview error:", error),
  });

  const commitMutation = useMutation({
    mutationFn: ({ selected, ids }: { selected: File; ids: number[] }) =>
      manifestApi.importCommit(selected, ids),
    onSuccess: (response) => {
      setResult(response.data);
      showSuccessNotification({ message: t("manifests.import.success") });
      onImported();
    },
    onError: (error) => logger.error("manifest import commit error:", error),
  });

  const toggleOverride = (hawbId: number, checked: boolean) =>
    setOverrideIds((ids) => (checked ? [...ids, hawbId] : ids.filter((id) => id !== hawbId)));

  const rowsByState = (state: ImportRowState): ImportPreviewRow[] =>
    preview?.rows.filter((row) => row.state === state) ?? [];

  const willWrite = (preview?.totals.new ?? 0) > 0 || overrideIds.length > 0;

  const renderRow = (row: ImportPreviewRow) => {
    const label = formatHawbLabel(row) || "—";
    const hawbId = row.hawb_id;
    return (
      <div key={row.row} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-b-0">
        {row.state === "changed" && hawbId !== undefined ? (
          <Checkbox
            className="pt-0.5"
            checked={overrideIds.includes(hawbId)}
            onChange={(e) => toggleOverride(hawbId, e.currentTarget.checked)}
            aria-label={t("manifests.import.override")}
          />
        ) : (
          <span className="w-5" />
        )}
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">{t("manifests.import.row", { row: row.row })}</span>
            <span className="font-semibold">{label}</span>
            {row.data.packages !== null && row.state !== "error" && (
              <span className="text-gray-500">
                · {t("manifests.import.packages", { count: row.data.packages })}
              </span>
            )}
          </div>
          {row.state === "error" && <span className="text-sm text-red-700">{row.error}</span>}
          {row.state === "changed" && (
            <HawbChangeSummary
              changes={row.changes ?? []}
              itemsToCreate={row.items_to_create ?? 0}
              itemsToRemove={row.items_to_remove ?? []}
            />
          )}
        </div>
      </div>
    );
  };

  const renderReview = () => {
    if (!preview) return null;
    if (preview.rows.length === 0) {
      return <span className="text-sm text-gray-500">{t("manifests.import.empty")}</span>;
    }
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {STATE_ORDER.map((state) => (
            <Badge key={state} color={STATE_COLORS[state]} variant="light">
              {t(`manifests.import.state.${state}`, { count: preview.totals[state] })}
            </Badge>
          ))}
        </div>
        <ScrollArea.Autosize mah={420}>
          <div className="flex flex-col gap-4 pr-3">
            {STATE_ORDER.filter((state) => state !== "unchanged").map((state) => {
              const rows = rowsByState(state);
              if (rows.length === 0) return null;
              return (
                <div key={state} className="flex flex-col">
                  <span className="text-sm font-semibold">
                    {t(`manifests.import.section.${state}`)}
                  </span>
                  {state === "changed" && (
                    <span className="text-xs text-gray-500">{t("manifests.import.changedHint")}</span>
                  )}
                  {rows.map(renderRow)}
                </div>
              );
            })}
          </div>
        </ScrollArea.Autosize>
      </div>
    );
  };

  const renderResult = () =>
    result && (
      <div className="flex flex-col gap-2 text-sm">
        <span>{t("manifests.import.result.created", { count: result.created })}</span>
        <span>{t("manifests.import.result.updated", { count: result.updated })}</span>
        <span>{t("manifests.import.result.unchanged", { count: result.unchanged })}</span>
        <span>{t("manifests.import.result.skipped", { count: result.skipped })}</span>
        {result.errors.length > 0 && (
          <div className="flex flex-col gap-1 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
            {result.errors.map((error) => (
              <span key={error.row}>
                {t("manifests.import.row", { row: error.row })}: {error.error}
              </span>
            ))}
          </div>
        )}
      </div>
    );

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      size="xl"
      title={t("manifests.import.title")}
      closeOnClickOutside={false}
      classNames={{ title: "font-bold" }}
    >
      <div className="flex flex-col gap-4">
        {result ? (
          renderResult()
        ) : preview ? (
          renderReview()
        ) : (
          <>
            <span className="text-sm text-gray-600">{t("manifests.import.description")}</span>
            <SelectFile accept=".xlsx,.csv" onFileSelect={setFile} />
            {previewMutation.isPending && <Progress value={progress} animated />}
          </>
        )}

        <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
          <Button size="sm" color="black" variant="outline" onClick={handleClose}>
            {result ? t("common.close") : t("common.cancel")}
          </Button>
          {!result &&
            (preview ? (
              <Button
                size="sm"
                color="black"
                disabled={!willWrite || !file}
                loading={commitMutation.isPending}
                onClick={() => file && commitMutation.mutate({ selected: file, ids: overrideIds })}
              >
                {t("manifests.import.confirm")}
              </Button>
            ) : (
              <Button
                size="sm"
                color="black"
                disabled={!file}
                loading={previewMutation.isPending}
                onClick={() => file && previewMutation.mutate(file)}
              >
                {t("manifests.import.review")}
              </Button>
            ))}
        </div>
      </div>
    </Modal>
  );
};

export default ImportManifestModal;
